import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import {
  FiHome,
  FiRefreshCw,
  FiTrendingUp,
  FiArrowRight,
  FiCheck,
  FiLayers,
} from "@/lib/icons";
import { broker } from "@/config/broker.config";

interface ServiceCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  features: string[];
}

const services: ServiceCard[] = [
  {
    title: "Buying a Home",
    description:
      "Whether it's your first home or your next, we'll help you find the right loan and guide you through every step of the purchase process.",
    href: "/home-loans/buying-a-home",
    icon: <FiHome className="size-6" />,
    features: [
      "Compare 70+ lenders",
      "Pre-approval in 24-48 hours",
      "Support through to settlement",
    ],
  },
  {
    title: "First Home Buyer",
    description:
      "Take advantage of government grants and schemes designed to help you get into the market sooner. We'll maximise your benefits.",
    href: "/home-loans/first-home-buyer",
    icon: <FiHome className="size-6" />,
    features: [
      "Up to $30,000 in grants",
      "5% deposit schemes",
      "Stamp duty concessions",
    ],
  },
  {
    title: "Refinancing",
    description:
      "Lower your rate, access equity, or consolidate debts. We'll compare your current loan against the market to find potential savings.",
    href: "/home-loans/refinancing",
    icon: <FiRefreshCw className="size-6" />,
    features: [
      "Rate comparison",
      "Equity access",
      "Debt consolidation",
    ],
  },
  {
    title: "Investment Property",
    description:
      "Build your property portfolio with the right loan structure. We understand investor needs and can help optimise your strategy.",
    href: "/home-loans/investment",
    icon: <FiTrendingUp className="size-6" />,
    features: [
      "Interest-only options",
      "Portfolio structuring",
      "Tax-effective strategies",
    ],
  },
  {
    title: "Construction Loans",
    description:
      "Building your dream home? Construction loans work differently. We'll find the right product and manage the progressive drawdown process.",
    href: "/home-loans/construction",
    icon: <FiHome className="size-6" />,
    features: [
      "Progress payments",
      "Land & build packages",
      "Fixed price contracts",
    ],
  },
  {
    title: "Bridging Loans",
    description:
      "Buy your new home before selling your current one. Bridging finance helps you avoid missing out while managing the timing of both transactions.",
    href: "/home-loans/bridging",
    icon: <FiLayers className="size-6" />,
    features: [
      "Buy before selling",
      "Flexible timing",
      "Move once, not twice",
    ],
  },
];

const stats = [
  { value: "70+", label: "Lenders" },
  { value: "$0", label: "Cost to you" },
  { value: "4.9", label: "Google rating" },
  { value: "24-48h", label: "Pre-approval" },
];

const process = [
  {
    step: 1,
    title: "Initial consultation",
    description:
      "We discuss your goals, assess your situation, and explain your options. This is free and no-obligation.",
  },
  {
    step: 2,
    title: "Loan comparison",
    description:
      "We compare products from 70+ lenders to find the best fit for your needs, not just the lowest rate.",
  },
  {
    step: 3,
    title: "Application & approval",
    description:
      "We prepare and submit your application, handle all lender communication, and keep you updated.",
  },
  {
    step: 4,
    title: "Settlement support",
    description:
      "We coordinate with your conveyancer and lender to ensure a smooth settlement. We're here until the keys are in your hand.",
  },
];

export function HomeLoansPage() {
  return (
    <>
      <SEO
        title="Home Loans Springfield, Brisbane & Logan"
        description="Home loans for buying, refinancing or investing in Springfield, Brisbane, Logan & Ipswich. Compare 70+ lenders with expert local broker guidance."
        url="/home-loans"
        keywords={[
          "home loans Springfield",
          "home loans Brisbane",
          "home loans Logan",
          "home loans Ipswich",
          "mortgage broker Springfield",
          "first home buyer Springfield",
          "refinance Brisbane",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Home Loans",
            description:
              "Home loans for owner-occupiers, first home buyers, refinancers and investors. Compared from 70+ Australian lenders.",
            url: "/home-loans",
            serviceType: "Home Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Home Loans</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Find the right home loan, <span className="italic">with expert guidance</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Whether you're buying your first home, refinancing, or building an
                investment portfolio, we'll compare 70+ lenders to find the best
                deal for your situation.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Get Started
                  <FiArrowRight className="size-4" />
                </Link>
                <Link
                  to="/calculators/borrowing-power"
                  className="inline-flex items-center gap-2 text-foreground px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  Check Borrowing Power
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-primary rounded-3xl p-8 text-primary-foreground">
                <p className="text-primary-foreground/90 text-sm uppercase tracking-wider mb-4">
                  Why choose us
                </p>
                <ul className="space-y-4">
                  {[
                    "Access to 70+ lenders in one application",
                    "Free service - we're paid by lenders",
                    "Legal duty to act in your best interest",
                    "Support from application to settlement",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-serif text-3xl md:text-4xl text-primary mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Our Services</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How can we help you?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every situation is different. Select the option that best describes
              your needs, or get in touch for personalised advice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.href}
                to={service.href}
                className="group bg-white rounded-2xl p-6 border border-border/50 hover:shadow-lg hover:border-primary/20 transition-all"
              >
                <div className="p-3 bg-cream rounded-xl inline-flex mb-4 group-hover:bg-accent/20 transition-colors">
                  <span className="text-primary">{service.icon}</span>
                </div>
                <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                  Learn more
                  <FiArrowRight className="size-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How it works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From first conversation to settlement, we make the home loan process
              simple and stress-free.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-white rounded-2xl p-6 h-full">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to get started?"
        title="Book a free consultation and let's find your perfect loan"
        description="No obligation, no cost. Just expert advice tailored to your situation."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
